import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useUploadDocument } from "../../../hooks/api/projectDocumentsApiHooks";

/**
 * Modal para upload de documento
 * @param {object} props
 * @param {boolean} props.isOpen - Se o modal está aberto
 * @param {function} props.onClose - Callback ao fechar
 * @param {object} props.documentType - Tipo de documento (DocumentTypeResponseDTO)
 * @param {number} props.projectId - ID do projeto
 * @param {File} props.initialFile - Arquivo inicial (drag-and-drop)
 * @param {function} props.onSuccess - Callback ao sucesso
 */
export default function DocumentUploadModal({
  isOpen,
  onClose,
  documentType,
  projectId,
  initialFile = null,
  onSuccess,
}) {
  const [selectedFile, setSelectedFile] = useState(initialFile);
  const [comments, setComments] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationError, setValidationError] = useState("");

  const { mutateAsync: uploadDocument, isLoading } = useUploadDocument();

  // Definir arquivo inicial
  useEffect(() => {
    if (initialFile) {
      setSelectedFile(initialFile);
      validateFile(initialFile);
    }
  }, [initialFile]);

  // Validação de arquivo
  const validateFile = (file) => {
    if (!file) {
      setValidationError("");
      return false;
    }

    // Validar extensão
    const extension = file.name.split(".").pop().toLowerCase();
    if (!documentType.allowedExtensions.includes(extension)) {
      const error = `Extensão não permitida. Aceitas: ${documentType.allowedExtensions
        .join(", ")
        .toUpperCase()}`;
      setValidationError(error);
      return false;
    }

    // Validar tamanho
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > documentType.maxSizeMB) {
      const error = `Arquivo muito grande. Tamanho máximo: ${documentType.maxSizeMB}MB`;
      setValidationError(error);
      return false;
    }

    setValidationError("");
    return true;
  };

  // Handler de seleção de arquivo
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      validateFile(file);
    }
  };

  // Handler de upload
  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Selecione um arquivo");
      return;
    }

    if (!validateFile(selectedFile)) {
      toast.error(validationError);
      return;
    }

    try {
      setUploadProgress(0);
      
      await uploadDocument({
        projectId,
        documentTypeId: documentType.id,
        file: selectedFile,
        comments,
        onUploadProgress: (progress) => {
          setUploadProgress(progress);
        },
      });

      toast.success("Documento enviado com sucesso!");
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error("Erro ao fazer upload:", error);
      const errorMessage =
        error.response?.data?.message || "Erro ao fazer upload do documento";
      toast.error(errorMessage);
    }
  };

  // Formatar tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(2)} MB` : `${(bytes / 1024).toFixed(2)} KB`;
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Upload: {documentType.name}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Seleção de Arquivo */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Arquivo
          </Typography>
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            fullWidth
            sx={{ mb: 1 }}
          >
            Selecionar Arquivo
            <input type="file" hidden onChange={handleFileChange} accept={documentType.allowedExtensions.map(ext => `.${ext}`).join(",")} />
          </Button>
          
          {selectedFile && (
            <Box
              sx={{
                p: 1.5,
                bgcolor: "background.default",
                borderRadius: 1,
                border: "1px solid",
                borderColor: validationError ? "error.main" : "divider",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                📄 {selectedFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(selectedFile.size)}
              </Typography>
            </Box>
          )}

          {validationError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
              {validationError}
            </Typography>
          )}
        </Box>

        {/* Comentários (opcional) */}
        <Box sx={{ mb: 2 }}>
          <TextField
            label="Comentários (opcional)"
            multiline
            rows={3}
            fullWidth
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Adicione comentários sobre este documento..."
            disabled={isLoading}
          />
        </Box>

        {/* Informações do tipo */}
        <Box
          sx={{
            p: 1.5,
            bgcolor: "info.lighter",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "info.main",
          }}
        >
          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
            <strong>Extensões permitidas:</strong>{" "}
            {documentType.allowedExtensions.join(", ").toUpperCase()}
          </Typography>
          <Typography variant="caption" display="block">
            <strong>Tamanho máximo:</strong> {documentType.maxSizeMB} MB
          </Typography>
        </Box>

        {/* Progress Bar */}
        {isLoading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="caption" sx={{ mt: 0.5, display: "block", textAlign: "center" }}>
              Enviando... {uploadProgress}%
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || !!validationError || isLoading}
          startIcon={<CloudUploadIcon />}
        >
          {isLoading ? "Enviando..." : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

